import { type api, useTeam } from '@vrplatform/data';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { day } from '@vrplatform/utils';
import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import { useEffect, useRef } from 'react';
import { resolveSalesTaxLiabilityTaxableLineIds } from './useSalesTaxLiabilityTableFilter.utils';

export type SalesTaxLiabilityOccupancyStatus =
  api['/reports/guest-balances']['get']['parameters']['query']['occupancyStatus'];

export function useSalesTaxLiabilityTableFilter() {
  const [{ id: teamId, effectiveStatementStartAt: statementStartAt }] =
    useTeam();
  const localStorageKey = `${teamId}-sales-tax-liability-taxable-line-ids`;

  const [taxableLineIds, setTaxableLineIds] = useLocalStorage<string[]>({
    key: localStorageKey,
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const previousLocalStorageKeyRef = useRef(localStorageKey);

  const [initial, setDate] = useQueryState('date', parseAsString);
  const [channels, setChannels] = useQueryState('channels', parseAsString);

  const startOfPreviousMonth = day().subtract(1, 'month').startOf('month');

  const defaultStart =
    statementStartAt && day(statementStartAt).isValid()
      ? startOfPreviousMonth.isBefore(day(statementStartAt), 'day')
        ? day(statementStartAt)
        : startOfPreviousMonth
      : startOfPreviousMonth;

  const defaultEnd = defaultStart.endOf('month').isAfter(day(), 'day')
    ? day()
    : defaultStart.endOf('month');

  const date =
    initial || `${defaultStart.yyyymmdd()}...${defaultEnd.yyyymmdd()}`;

  const [start, end] = date.split('...');
  const scopedTaxableLineIds = resolveSalesTaxLiabilityTaxableLineIds({
    taxableLineIds,
    localStorageKey,
    previousLocalStorageKey: previousLocalStorageKeyRef.current,
    readTaxableLineIds: (key) =>
      readLocalStorageValue<string[]>({
        key,
        defaultValue: [],
      }),
  });

  useEffect(() => {
    previousLocalStorageKeyRef.current = localStorageKey;
  }, [localStorageKey]);

  return {
    localStorageKey,
    filter: {
      taxableLineIds: scopedTaxableLineIds,
      date,
      endDate: end,
      startDate: start,
      channels: channels?.split(',') ?? [],
    },
    setDate,
    setTaxableLineIds,
    setChannels,
  };
}
